import constants from '../constants';
import { EditableType } from '../../../constants';
import * as helpers from '../../helpers';
import { TagName, MediaElementSources, MediaElementData, MediaElementConfig, MediaElement } from '../../editableMedia/typing';
import { getVideoSettings } from '../../editableMedia/getData';
import { DuplicatedItemInSpreadConfig } from '../html';

interface FrameData {
	frameWidth: number;
	frameHeight: number;
}

interface IndesignMediaElementData extends MediaElementData {
	className: string;
}

export type IndesignMediaElementConfig = IndesignMediaElementData & MediaElementConfig;

interface MediaData {
	items: IndesignMediaElementConfig[];
	duplicatedItemsInSpread: DuplicatedItemInSpreadConfig[];
	isError: boolean;
}

export enum MediaElementDataAttribute {
	edited = 'data-edited', // is set to media when it was modified in backoffice
	label = 'data-label', // is set to store media name
	defaultLabel = 'data-default-label', // is set to store default media name
	tooltip = 'data-tooltip', // is set to store media tooltip text
	tooltipActive = 'data-tooltip-active', // is set when tooltip is active
	defaultLink = 'data-default-link', // is set to media to store default link
	driveLink = 'data-drive-link', // is set to media to store drive link
	externalLink = 'data-external-link', // is set to media to store external link
	externalLinkActive = 'data-external-link-active', // is set when external link is active
	hidden = 'data-hidden', // is set when media is hidden
}

const isImageElement = (className: string): boolean => (
	className.includes(constants.imageClassName)
);

const isVideoElement = (tagName: string): boolean => tagName === TagName.video;

const isVideoIframeElement = (tagName: string, className: string): boolean => (
	tagName === TagName.iframe && className.includes(constants.videoClassName)
);

const getFrameData = (id: string, css: string): FrameData => {
	const { width, height } = helpers.getElementSizes(id, css);
	return {
		frameWidth: width,
		frameHeight: height,
	};
};

const getMediaType = ({ tagName, className }: MediaElement): EditableType => {
	switch (tagName) {
		case TagName.img:
		default:
			return EditableType.image;
		case TagName.video:
			return EditableType.video;
		case TagName.iframe:
			return className.includes(constants.videoClassName)
				? EditableType.video
				: EditableType.animation;
	}
};

const getElementId = (element: MediaElement): string => {
	if (isImageElement(element.className)) {
		return element.id;
	}

	return helpers.getElementId(element);
};

const getElementSources = (element: MediaElement, isEditedFromBackoffice: boolean, isVisible: boolean): MediaElementSources => {
	if (isEditedFromBackoffice) {
		return {
			defaultLink: element.getAttribute(MediaElementDataAttribute.defaultLink) || '',
			driveLink: element.getAttribute(MediaElementDataAttribute.driveLink) || '',
			externalLink: element.getAttribute(MediaElementDataAttribute.externalLink) || '',
			isExternalLinkActive: element.getAttribute(MediaElementDataAttribute.externalLinkActive) === 'true',
		};
	}

	const { tagName, className } = element;
	const elementWithSrc = isVideoElement(tagName)
		? element.querySelector('source')
		: element;
	const { src } = elementWithSrc as MediaElement;
	const isVideoIframe = isVideoIframeElement(tagName, className);
	const defaultSrc = element.getAttribute(MediaElementDataAttribute.defaultLink) || '';
	const defaultLink = helpers.getUrlWithoutOrigin(isVisible ? src : defaultSrc);

	return {
		defaultLink,
		driveLink: '',
		externalLink: isVideoIframe ? defaultLink : '',
		isExternalLinkActive: isVideoIframe,
	};
};

const getElementLabels = (element: MediaElement, isEditedFromBackoffice: boolean): { label: string, defaultLabel: string } => {
	if (!isEditedFromBackoffice) {
		return {
			label: '',
			defaultLabel: element.getAttribute(MediaElementDataAttribute.label) || '',
		};
	}

	return {
		label: element.getAttribute(MediaElementDataAttribute.label) || '',
		defaultLabel: element.getAttribute(MediaElementDataAttribute.defaultLabel) || '',
	};
};

const getElementTooltip = (element: MediaElement, isEditedFromBackoffice: boolean): { tooltip: string, isTooltipActive: boolean } => {
	const tooltipActionAttribute = element.attributes['onmouseenter'] || {}; // tslint:disable-line no-string-literal
	const { value: defaultTooltipAction } = tooltipActionAttribute;
	const tooltip = element.getAttribute(MediaElementDataAttribute.tooltip) || helpers.getDefaultTooltip(defaultTooltipAction);

	return {
		tooltip,
		isTooltipActive: isEditedFromBackoffice
			? element.getAttribute(MediaElementDataAttribute.tooltipActive) === 'true'
			: !!tooltip,
	};
};

const getMediaData = (element: MediaElement, frameWidth: number, frameHeight: number): MediaElementData => {
	const mediaElement = isImageElement(element.className)
		? element.querySelector('img')
		: element;
	const {
		style: { width, height, transform },
	} = mediaElement as MediaElement;
	const [x, y] = helpers.getTranslateValueFromStyle({ transform });
	const isEditedFromBackoffice = mediaElement!.getAttribute(MediaElementDataAttribute.edited) === 'true';
	const isVisible = !(mediaElement!.getAttribute(MediaElementDataAttribute.hidden) &&
		mediaElement!.getAttribute(MediaElementDataAttribute.hidden) === 'true');
	const defaultSrc = mediaElement!.getAttribute(MediaElementDataAttribute.defaultLink) || '';
	const { className, tagName } = element;

	return {
		type: getMediaType(element),
		width: width ? helpers.getNumberFromString(width) : frameWidth,
		height: height ? helpers.getNumberFromString(height) : frameHeight,
		x,
		y,
		isEditedFromBackoffice,
		isVisible,
		canDragAndResize: isImageElement(element.className)
			? isEditedFromBackoffice
			: false,
		isInsideButton: !!element.closest(`[class*=${constants.buttonClassName}]`),
		videoSettings: getVideoSettings(mediaElement as MediaElement, isVideoElement(tagName), isVideoIframeElement(tagName, className), defaultSrc),
		...getElementSources(mediaElement as MediaElement, isEditedFromBackoffice, isVisible),
		...getElementLabels(mediaElement as MediaElement, isEditedFromBackoffice),
		...getElementTooltip(mediaElement as MediaElement, isEditedFromBackoffice),
	};
};

const getConfig = (
	element: MediaElement,
	page: number,
	pageIndexInSpread: number,
	css: string,
): IndesignMediaElementConfig | DuplicatedItemInSpreadConfig | undefined => {
	const id = getElementId(element);
	const className = element.className.split(' ').find(className => (
		className.includes(constants.imageClassName) ||
		className.includes(constants.videoClassName) ||
		className.includes(constants.animationClassName)
	)) || '';

	if (helpers.isDuplicatedItemInSpread(className, pageIndexInSpread)) {
		return {
			page,
			id,
			className,
		};
	}

	const { frameWidth, frameHeight } = getFrameData(id, css);
	const width = frameWidth ? frameWidth : 0;
	const height = frameHeight ? frameHeight : 0;
	const mediaData = getMediaData(element, width, height);

	return {
		page,
		id,
		className,
		importManagerId: `${id}-${page}`,
		frameWidth: frameWidth,
		frameHeight: frameHeight,
		isLoaded: true,
		...mediaData,
	};
};

export default (page: number, pageIndexInSpread: number, htmlDocument: Document, css: string): MediaData => {
	const items: IndesignMediaElementConfig[] = [];
	const duplicatedItemsInSpread: DuplicatedItemInSpreadConfig[] = [];
	let isError: boolean = false;

	const editableMediaElements = ([
		...htmlDocument.querySelectorAll(`[class*=${constants.imageClassName}]:not([class*=${constants.lockedClassName}])`),
		...htmlDocument.querySelectorAll(`[class*=${constants.videoClassName}]:not([class*=${constants.lockedClassName}])`),
		...htmlDocument.querySelectorAll(`[class*=${constants.animationClassName}]:not([class*=${constants.lockedClassName}])`),
	] as MediaElement[]).filter(element => !helpers.hasLockedParent(element));

	try {
		editableMediaElements.forEach(element => {
			const config = getConfig(element, page, pageIndexInSpread, css);

			if (config) {
				if ('frameWidth' in config) {
					items.push(config);
				} else {
					duplicatedItemsInSpread.push(config);
				}
			}
		});
	} catch (err) {
		isError = true;
	}

	return {
		items,
		duplicatedItemsInSpread,
		isError,
	};
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/Editor/utils/indesignEditor/editableMedia/getData.ts